/**
 * ReleaseTourHelpMenu — the top bar's Help icon + its "replay a walkthrough" dropdown. Help moved up
 * from the rail to the top bar (Antonin, 12 Oct, both apps): a 32px quiet icon button, menu below.
 *
 * Lists every registered release tour (via the registry) so a user can replay one on demand,
 * even after its "show once" flag has already fired.
 *
 * `<ReleaseTour>` is mounted unconditionally here, not inside the dropdown — closing the
 * dropdown (which happens the instant a tour is picked) must not unmount the Joyride instance
 * that's about to run. Only `run`/`steps`/`onEvent` change; the dropdown's open/closed state is
 * a separate concern.
 */
import { useEffect, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { ReleaseTour } from './release-tour';
import { useReleaseTour } from './use-release-tour';
import { getRegisteredTours } from './release-tour-registry';
import type { ReleaseTourConfig } from './release-tour-types';

const EMPTY_TOUR: ReleaseTourConfig = { id: '__none__', name: '', enabled: false, steps: [] };

export function ReleaseTourHelpMenu() {
  const [open, setOpen] = useState(false);
  const registered = getRegisteredTours();
  // Keyed by a fresh object per pick (not the bare id) so re-picking the SAME tour still
  // replays — `setActiveId(sameId)` would not change state, so the start-effect below would
  // never re-fire and the second replay would silently do nothing.
  const [pick, setPick] = useState<{ id: string } | null>(null);
  const activeId = pick?.id ?? null;
  const active = registered.find((t) => t.id === activeId) ?? registered[0] ?? EMPTY_TOUR;
  const tour = useReleaseTour(active, { autoStart: false });

  // `tour.start()` is opaque to the effect (only its own internal setState matters, not this
  // effect's), but `setOpen` is called directly here — do that eagerly in the click handler
  // below instead, so this effect only performs the one legitimate side effect: starting the
  // tour once `active` has updated to reflect the freshly-picked config.
  useEffect(() => {
    if (!pick) return;
    tour.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick]);

  function pickTour(id: string) {
    setPick({ id });
    setOpen(false);
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Help"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-help-menu
        className="grid size-8 place-items-center rounded-input text-[var(--ink-secondary)] hover:bg-[var(--surface-read)] hover:text-[var(--ink-body)]"
      >
        <HelpCircle className="size-5" aria-hidden />
      </button>
      {open && (
        <>
          {/* Click-outside dismiss. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-10 right-0 z-50 w-56 rounded-input border border-[var(--surface-line)] bg-[var(--surface-read)] p-2 shadow-overlay">
            <p className="mb-1 px-2 text-xs font-medium text-[var(--ink-secondary)]">
              Replay a walkthrough
            </p>
            {registered.length === 0 ? (
              <p className="px-2 py-1 text-xs text-text-muted">No walkthroughs available yet.</p>
            ) : (
              registered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => pickTour(t.id)}
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-text-primary hover:bg-surface-hover"
                >
                  {t.name}
                </button>
              ))
            )}
          </div>
        </>
      )}
      <ReleaseTour run={tour.run} steps={tour.steps} onEvent={tour.onEvent} />
    </div>
  );
}
